/**
 * The one-shot handoff that makes /book/free-trial/success reachable only by
 * actually booking (REQ-2).
 *
 * The booking form stashes this right after the backend confirms the booking
 * and then navigates to the success route. The success page renders only
 * while the stash is present; a direct visit, a bookmark or a link from
 * anywhere else finds nothing and is sent back to the form. sessionStorage is
 * the store for the same reason gtmSignup uses it: it is per-tab and dies with
 * the tab.
 *
 * The stash is kept (not consumed) while the success page is open so a
 * refresh re-renders the confirmation; nothing is re-fired by that, because
 * the Schedule event is claimed from the server exactly once. It expires so a
 * much later visit in the same tab does not resurrect an old confirmation.
 */

const STORAGE_KEY = "free_trial_booked";

export const FREE_TRIAL_SUCCESS_PATH = "/book/free-trial/success";

const MAX_AGE_MS = 30 * 60 * 1000;

export function stashFreeTrialBookingSuccess(now: number = Date.now()): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ bookedAt: now }));
  } catch {
    // Storage blocked: the success page will fall back to the form, the
    // booking itself is unaffected.
  }
}

export function hasFreshFreeTrialBookingSuccess(now: number = Date.now()): boolean {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return false;
    }

    const bookedAt = Number((JSON.parse(raw) as { bookedAt?: unknown })?.bookedAt);
    const fresh = Number.isFinite(bookedAt) && now - bookedAt >= 0 && now - bookedAt <= MAX_AGE_MS;

    if (!fresh) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }

    return fresh;
  } catch {
    return false;
  }
}

export function clearFreeTrialBookingSuccess(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Everything that follows a booking the backend has confirmed, in order:
 * leave the proof for the success page, ask the pixel to claim the Schedule
 * the backend just recorded, then go to the success URL. Called only from the
 * confirm call's success path - never from a click or submit handler.
 */
export function completeFreeTrialBooking(
  navigate: (href: string) => void,
  successHref: string,
  flushPixel: () => void,
): void {
  stashFreeTrialBookingSuccess();
  flushPixel();
  navigate(successHref);
}
